import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, Search, GripVertical } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import { getPublicImageUrl } from '../../lib/media';
import { toast } from 'sonner';


export default function DishesPage() {
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editDish, setEditDish] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const [draggedDishId, setDraggedDishId] = useState(null);
  const [reorderingCategories, setReorderingCategories] = useState(false);
  const [reorderingDishes, setReorderingDishes] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', image: '', order: 0, active: true, visible_dine_in: true, visible_delivery: true, visible_takeaway: true });
  const [form, setForm] = useState({
    name: '',
    description: '',
    category_id: '',
    image: '',
    order: 0,
    prices: { dine_in: 0, delivery: 0, takeaway: 0 },
    ingredients: '',
    allergens: '',
    available: true,
    supplements: [],
    dish_type: 'standard',
    formula_groups: [],
    vat_rate: '',
  });

  const load = () => {
    Promise.all([
      api.get(`/categories`),
      api.get(`/dishes`)
    ]).then(([c, d]) => { setCategories(c.data); setDishes(d.data); });
  };
  useEffect(load, []);

  const openNew = () => {
    setEditDish(null);
    setImagePreview('');
    setImageFile(null);
    setForm({
      name: '',
      description: '',
      category_id: categories[0]?.id || '',
      image: '',
      order: dishes.length + 1,
      prices: { dine_in: 0, delivery: 0, takeaway: 0 },
      ingredients: '',
      allergens: '',
      available: true,
      supplements: [],
      dish_type: 'standard',
      formula_groups: [],
      vat_rate: '',
    });
    setShowDialog(true);
  };
  const openEdit = (d) => {
    setEditDish(d);
    setImagePreview(d.image || '');
    setImageFile(null);
    setForm({
      name: d.name,
      description: d.description,
      category_id: d.category_id,
      image: d.image || '',
      order: d.order || 0,
      prices: d.prices || { dine_in: 0, delivery: 0, takeaway: 0 },
      ingredients: d.ingredients,
      allergens: d.allergens,
      available: d.available,
      supplements: d.supplements || [],
      dish_type: d.dish_type || 'standard',
      formula_groups: d.formula_groups || [],
      vat_rate: d.vat_rate ?? '',
    });
    setShowDialog(true);
  };

  const openNewCategory = () => {
    setEditCategory(null);
    setCategoryForm({ name: '', description: '', image: '', order: categories.length + 1, active: true, visible_dine_in: true, visible_delivery: true, visible_takeaway: true });
    setShowCategoryDialog(true);
  };

  const openEditCategory = (category) => {
    setEditCategory(category);
    setCategoryForm({
      name: category.name || '',
      description: category.description || '',
      image: category.image || '',
      order: category.order || 0,
      active: category.active !== false,
      visible_dine_in: category.visible_dine_in !== false,
      visible_delivery: category.visible_delivery !== false,
      visible_takeaway: category.visible_takeaway !== false,
    });
    setShowCategoryDialog(true);
  };

  const handleImageUpload = (file) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      setImagePreview(result);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      if (form.dish_type === 'formula') {
        const invalidGroup = (form.formula_groups || []).find(group => !(group.source_dish_ids || []).length);
        if (invalidGroup) {
          toast.error(`La sous-categorie "${invalidGroup.name || 'sans nom'}" doit contenir au moins un plat autorise`);
          return;
        }
      }
      let imageUrl = form.image;
      if (imageFile) {
        const uploadData = new FormData();
        uploadData.append('image', imageFile);
        const uploadResponse = await api.post('/uploads/dishes', uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        imageUrl = uploadResponse.data.url;
      }

      const payload = {
        ...form,
        image: imageUrl,
        prices: {
          dine_in: parseFloat(form.prices.dine_in) || 0,
          delivery: parseFloat(form.prices.delivery) || 0,
          takeaway: parseFloat(form.prices.takeaway) || 0
        },
        order: parseInt(form.order, 10) || 0,
        formula_groups: (form.formula_groups || []).map(group => ({
          ...group,
          selection_min: Math.max(0, parseInt(group.selection_min, 10) || 0),
          selection_max: Math.max(0, parseInt(group.selection_max, 10) || 0),
          extra_prices: Object.fromEntries(
            Object.entries(group.extra_prices || {}).map(([dishId, price]) => [dishId, parseFloat(price) || 0])
          ),
        })),
        vat_rate: form.vat_rate === '' ? null : (parseFloat(form.vat_rate) || 0),
      };
      if (editDish && !payload.image) {
        payload.image = editDish.image || '';
      }
      if (editDish) {
        await api.put(`/dishes/${editDish.id}`, payload);
        toast.success('Plat modifie');
      } else {
        await api.post(`/dishes`, payload);
        toast.success('Plat cree');
      }
      setImageFile(null);
      setShowDialog(false); load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de l'envoi de l'image ou de l'enregistrement");
    }
  };

  const saveCategory = async () => {
    try {
      const payload = {
        ...categoryForm,
        order: parseInt(categoryForm.order, 10) || 0,
        active: !!categoryForm.active,
        visible_dine_in: categoryForm.visible_dine_in !== false,
        visible_delivery: categoryForm.visible_delivery !== false,
        visible_takeaway: categoryForm.visible_takeaway !== false,
      };
      if (editCategory) {
        await api.put(`/categories/${editCategory.id}`, payload);
        toast.success('Categorie modifiee');
      } else {
        await api.post(`/categories`, payload);
        toast.success('Categorie creee');
      }
      setShowCategoryDialog(false);
      load();
    } catch {
      toast.error('Erreur categorie');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce plat ?')) return;
    await api.delete(`/dishes/${id}`);
    toast.success('Supprime'); load();
  };

  const delCategory = async (id) => {
    if (!window.confirm('Supprimer cette categorie ?')) return;
    await api.delete(`/categories/${id}`);
    toast.success('Categorie supprimee');
    load();
  };

  const removeDishImage = async () => {
    if (!editDish) {
      setForm(prev => ({ ...prev, image: '' }));
      setImagePreview('');
      setImageFile(null);
      return;
    }
    try {
      await api.delete(`/dishes/${editDish.id}/image`);
      setForm(prev => ({ ...prev, image: '' }));
      setImagePreview('');
      setImageFile(null);
      toast.success("Image supprimee");
      load();
    } catch {
      toast.error("Impossible de supprimer l'image");
    }
  };

  const addFormulaGroup = () => {
    setForm(prev => ({
      ...prev,
      formula_groups: [
        ...(prev.formula_groups || []),
        {
          id: `group-${Date.now()}`,
          name: '',
          source_category_ids: [],
          source_dish_ids: [],
          selection_min: 1,
          selection_max: 1,
          extra_prices: {},
        }
      ],
    }));
  };

  const updateFormulaGroup = (index, nextGroup) => {
    setForm(prev => ({
      ...prev,
      formula_groups: prev.formula_groups.map((group, groupIndex) => groupIndex === index ? nextGroup : group),
    }));
  };

  const removeFormulaGroup = (index) => {
    setForm(prev => ({
      ...prev,
      formula_groups: prev.formula_groups.filter((_, groupIndex) => groupIndex !== index),
    }));
  };

  const updateFormulaDishExtraPrice = (groupIndex, group, dishId, value) => {
    updateFormulaGroup(groupIndex, {
      ...group,
      extra_prices: {
        ...(group.extra_prices || {}),
        [dishId]: value,
      },
    });
  };

  const getDishReferencePrice = (dish) => {
    const dineInPrice = Number(dish?.prices?.dine_in || 0);
    return dineInPrice.toFixed(2);
  };

  const filtered = dishes.filter(d => (filter === 'all' || d.category_id === filter) && (!search || d.name.toLowerCase().includes(search.toLowerCase())));
  const getCatName = (id) => categories.find(c => c.id === id)?.name || '';
  const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
  const sortedFilteredDishes = [...filtered].sort((a, b) => (a.order || 0) - (b.order || 0));

  const reorderCategories = async (nextCategories) => {
    const normalized = nextCategories.map((category, index) => ({
      ...category,
      order: index + 1,
    }));
    const previousCategories = categories;
    setCategories(normalized);
    setReorderingCategories(true);
    try {
      await Promise.all(normalized.map((category) => (
        api.put(`/categories/${category.id}`, {
          name: category.name || '',
          description: category.description || '',
          image: category.image || '',
          order: category.order || 0,
          active: category.active !== false,
          visible_dine_in: category.visible_dine_in !== false,
          visible_delivery: category.visible_delivery !== false,
          visible_takeaway: category.visible_takeaway !== false,
        })
      )));
      toast.success("Ordre des categories mis a jour");
    } catch {
      setCategories(previousCategories);
      toast.error("Impossible de reordonner les categories");
    } finally {
      setReorderingCategories(false);
    }
  };

  const handleCategoryDrop = async (targetCategoryId) => {
    if (!draggedCategoryId || draggedCategoryId === targetCategoryId) {
      setDraggedCategoryId(null);
      return;
    }
    const currentCategories = [...sortedCategories];
    const draggedIndex = currentCategories.findIndex(category => category.id === draggedCategoryId);
    const targetIndex = currentCategories.findIndex(category => category.id === targetCategoryId);
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedCategoryId(null);
      return;
    }
    const reordered = [...currentCategories];
    const [draggedCategory] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, draggedCategory);
    setDraggedCategoryId(null);
    await reorderCategories(reordered);
  };

  const buildDishPayload = (dish, order) => ({
    name: dish.name || '',
    description: dish.description || '',
    category_id: dish.category_id || '',
    image: dish.image || '',
    order,
    prices: {
      dine_in: parseFloat(dish.prices?.dine_in) || 0,
      delivery: parseFloat(dish.prices?.delivery) || 0,
      takeaway: parseFloat(dish.prices?.takeaway) || 0,
    },
    ingredients: dish.ingredients || '',
    allergens: dish.allergens || '',
    available: dish.available !== false,
    supplements: dish.supplements || [],
    dish_type: dish.dish_type || 'standard',
    formula_groups: dish.formula_groups || [],
    vat_rate: dish.vat_rate ?? null,
  });

  const reorderDishes = async (nextDishes) => {
    const normalized = nextDishes.map((dish, index) => ({
      ...dish,
      order: index + 1,
    }));
    const previousDishes = dishes;
    setDishes(current => current.map(dish => normalized.find(item => item.id === dish.id) || dish));
    setReorderingDishes(true);
    try {
      await Promise.all(normalized.map((dish) => api.put(`/dishes/${dish.id}`, buildDishPayload(dish, dish.order))));
      toast.success('Ordre des plats mis a jour');
    } catch {
      setDishes(previousDishes);
      toast.error('Impossible de reordonner les plats');
    } finally {
      setReorderingDishes(false);
    }
  };

  const handleDishDrop = async (targetDishId) => {
    if (!draggedDishId || draggedDishId === targetDishId) {
      setDraggedDishId(null);
      return;
    }
    const currentDishes = [...sortedFilteredDishes];
    const draggedIndex = currentDishes.findIndex(dish => dish.id === draggedDishId);
    const targetIndex = currentDishes.findIndex(dish => dish.id === targetDishId);
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedDishId(null);
      return;
    }
    const reordered = [...currentDishes];
    const [draggedDish] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, draggedDish);
    setDraggedDishId(null);
    await reorderDishes(reordered);
  };

  return (
    <div data-testid="admin-dishes-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-2xl font-bold text-[#2A2A28]">Plats & Menu</h1>
        <div className="flex gap-2">
          <Button onClick={openNewCategory} variant="outline" className="rounded-xl border-[#5B6B3F] text-[#5B6B3F]" data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Ajouter une categorie
          </Button>
          <Button onClick={openNew} className="rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="add-dish-btn">
            <Plus className="w-4 h-4 mr-1" /> Ajouter un plat
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-[#EBE8E0] bg-white p-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-semibold text-[#2A2A28]">Categories</h2>
            <p className="text-xs text-[#7A7A75] mt-1">
              Glisse-depose les cartes pour changer l'ordre d'affichage plus facilement.
            </p>
          </div>
          <span className="text-xs text-[#7A7A75]">La categorie Formules sert a afficher les offres. Chaque formule choisit ensuite ses plats autorises.</span>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {sortedCategories.map(category => (
            <div
              key={category.id}
              draggable={!reorderingCategories}
              onDragStart={() => setDraggedCategoryId(category.id)}
              onDragEnd={() => setDraggedCategoryId(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleCategoryDrop(category.id)}
              className={`rounded-xl border p-4 transition ${draggedCategoryId === category.id ? 'border-[#5B6B3F] bg-[#5B6B3F]/6 opacity-70' : 'border-[#EBE8E0]'} ${reorderingCategories ? 'cursor-wait' : 'cursor-grab'}`}
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-lg border border-[#EBE8E0] bg-[#FAF8F5] p-2 text-[#7A7A75]">
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <h3 className="font-medium text-[#2A2A28]">{category.name}</h3>
                  <p className="text-xs text-[#7A7A75]">Position: {category.order || 0}</p>
                </div>
                <Badge className={category.active !== false ? 'bg-[#5B6B3F]/10 text-[#5B6B3F]' : 'bg-[#8C3A3A]/10 text-[#8C3A3A]'}>
                  {category.active !== false ? 'Visible' : 'Masquee'}
                </Badge>
              </div>
              <p className="mb-3 text-sm text-[#7A7A75]">{category.description || 'Aucune description'}</p>
              <p className="mb-3 text-xs text-[#7A7A75]">
                Affichage: {category.visible_dine_in !== false ? 'Sur place' : ''}{category.visible_dine_in !== false && (category.visible_delivery !== false || category.visible_takeaway !== false) ? ' / ' : ''}{category.visible_delivery !== false ? 'Livraison' : ''}{category.visible_delivery !== false && category.visible_takeaway !== false ? ' / ' : ''}{category.visible_takeaway !== false ? 'Emporter' : ''}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-lg border-[#EBE8E0] text-[#7A7A75]" onClick={() => openEditCategory(category)}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => delCategory(category.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A75]" />
          <Input className="pl-9 rounded-xl border-[#EBE8E0]" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} data-testid="dishes-search" />
        </div>
        <select className="h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">Toutes categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedFilteredDishes.map(dish => (
          <div
            key={dish.id}
            draggable={!reorderingDishes}
            onDragStart={() => setDraggedDishId(dish.id)}
            onDragEnd={() => setDraggedDishId(null)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => handleDishDrop(dish.id)}
            className={`bg-white rounded-xl border border-[#EBE8E0] overflow-hidden transition ${draggedDishId === dish.id ? 'border-[#5B6B3F] bg-[#5B6B3F]/6 opacity-70' : ''} ${reorderingDishes ? 'cursor-wait' : 'cursor-grab'}`}
            data-testid={`admin-dish-${dish.id}`}
          >
            {dish.image && <img src={getPublicImageUrl(dish.image)} alt={dish.name} className="w-full h-40 object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 rounded-lg border border-[#EBE8E0] bg-[#FAF8F5] p-1.5 text-[#7A7A75]">
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2A2A28]">{dish.name}</h3>
                    <p className="text-[11px] text-[#9A968C]">Position: {dish.order || 0}</p>
                  </div>
                </div>
                <Badge className={`text-xs ${dish.available ? 'bg-[#5B6B3F]/10 text-[#5B6B3F]' : 'bg-[#8C3A3A]/10 text-[#8C3A3A]'}`}>{dish.available ? 'Dispo' : 'Indispo'}</Badge>
              </div>
              <p className="text-xs text-[#7A7A75] mb-2">{getCatName(dish.category_id)}</p>
              {dish.dish_type === 'formula' && <p className="text-xs text-[#5B6B3F] mb-2">Formule personnalisable</p>}
              <div className="flex gap-3 text-xs mb-3">
                <span>SP: {dish.prices?.dine_in?.toFixed(2)}EUR</span>
                <span>LIV: {dish.prices?.delivery?.toFixed(2)}EUR</span>
                <span>EMP: {dish.prices?.takeaway?.toFixed(2)}EUR</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-lg border-[#EBE8E0] text-[#7A7A75]" onClick={() => openEdit(dish)} data-testid={`edit-dish-${dish.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => del(dish.id)} data-testid={`delete-dish-${dish.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editDish ? 'Modifier le plat' : 'Nouveau plat'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Nom</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="dish-form-name" /></div>
            <div><Label className="text-sm">Description</Label><Textarea className="mt-1 rounded-xl border-[#EBE8E0]" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div><Label className="text-sm">Categorie</Label>
              <select className="w-full mt-1 h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm" value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})} data-testid="dish-form-category">
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm">Type de fiche</Label>
              <select className="w-full mt-1 h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm" value={form.dish_type} onChange={e => setForm({ ...form, dish_type: e.target.value, formula_groups: e.target.value === 'formula' ? form.formula_groups : [] })}>
                <option value="standard">Plat standard</option>
                <option value="formula">Formule a composer depuis les autres categories</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Image locale</Label>
              <Input
                type="file"
                accept="image/*"
                className="mt-1 rounded-xl border-[#EBE8E0]"
                onChange={e => handleImageUpload(e.target.files?.[0])}
                data-testid="dish-form-image-file"
              />
              {imagePreview && (
                <div className="rounded-xl overflow-hidden border border-[#EBE8E0] bg-[#FAF8F5]">
                  <img src={imagePreview} alt="Apercu" className="w-full h-40 object-cover" />
                </div>
              )}
              {(imagePreview || form.image) && (
                <Button type="button" variant="outline" className="rounded-xl border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={removeDishImage}>
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer l'image
                </Button>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div><Label className="text-xs">Sur place (EUR)</Label><Input type="number" step="0.01" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.prices.dine_in} onChange={e => setForm({...form, prices: {...form.prices, dine_in: e.target.value}})} data-testid="dish-form-price-dinein" /></div>
              <div><Label className="text-xs">Livraison (EUR)</Label><Input type="number" step="0.01" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.prices.delivery} onChange={e => setForm({...form, prices: {...form.prices, delivery: e.target.value}})} /></div>
              <div><Label className="text-xs">Emporter (EUR)</Label><Input type="number" step="0.01" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.prices.takeaway} onChange={e => setForm({...form, prices: {...form.prices, takeaway: e.target.value}})} /></div>
            </div>
            <div>
              <Label className="text-sm">Ordre d'affichage du plat</Label>
              <Input type="number" min="0" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.order} onChange={e => setForm({...form, order: e.target.value})} />
              <p className="mt-1 text-xs text-[#7A7A75]">Astuce: pour reordonner plus vite, ferme cette fenetre et glisse-depose les cartes de plats.</p>
            </div>
            <div><Label className="text-sm">TVA du produit (%)</Label><Input type="number" step="0.01" className="mt-1 rounded-xl border-[#EBE8E0]" value={form.vat_rate} onChange={e => setForm({...form, vat_rate: e.target.value})} placeholder="Vide = TVA par defaut" /></div>
            <div><Label className="text-sm">Ingredients</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={form.ingredients} onChange={e => setForm({...form, ingredients: e.target.value})} /></div>
            <div><Label className="text-sm">Allergenes</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={form.allergens} onChange={e => setForm({...form, allergens: e.target.value})} /></div>
            {form.dish_type === 'formula' && (
              <div className="space-y-4 rounded-2xl border border-[#EBE8E0] p-4">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div>
                    <Label className="text-sm font-semibold text-[#2A2A28]">Composition de la formule</Label>
                    <p className="text-xs text-[#7A7A75]">La formule choisit des plats precis. Exemple: falafel, shawarma viande, shawarma poulet, labneh et le client n'en choisit qu'un.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 xl:justify-end">
                    <Button type="button" variant="outline" className="rounded-lg border-[#5B6B3F] text-[#5B6B3F]" onClick={addFormulaGroup}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un choix
                    </Button>
                  </div>
                </div>
                {(form.formula_groups || []).map((group, index) => (
                  <div key={group.id} className="rounded-xl border border-[#EBE8E0] p-4">
                    <div className="mb-3 grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(260px,1fr)]">
                      <div>
                        <Label className="text-xs">Nom du choix</Label>
                        <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={group.name} onChange={e => updateFormulaGroup(index, { ...group, name: e.target.value })} placeholder="Ex: Dessert" />
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label className="text-xs">Minimum obligatoire</Label>
                          <Input type="number" min="0" className="mt-1 rounded-xl border-[#EBE8E0]" value={group.selection_min} onChange={e => updateFormulaGroup(index, { ...group, selection_min: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Nombre inclus dans la formule</Label>
                          <Input type="number" min="0" className="mt-1 rounded-xl border-[#EBE8E0]" value={group.selection_max} onChange={e => updateFormulaGroup(index, { ...group, selection_max: e.target.value })} />
                        </div>
                      </div>
                    </div>
                    <Label className="text-xs">Plats autorises dans cette sous-categorie</Label>
                    <div className="mt-2 grid gap-2 md:grid-cols-2">
                      {dishes
                        .filter(dish => dish.id !== editDish?.id)
                        .map(dish => {
                        const selected = group.source_dish_ids?.includes(dish.id);
                        const configuredExtra = (group.extra_prices || {})[dish.id] ?? 0;
                        return (
                          <div
                            key={dish.id}
                            className={`rounded-xl border px-3 py-3 text-left text-sm ${
                              selected ? 'border-[#5B6B3F] bg-[#5B6B3F]/8 text-[#2A2A28]' : 'border-[#EBE8E0] bg-white text-[#7A7A75]'
                            }`}
                          >
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                              <button
                                type="button"
                                onClick={() => updateFormulaGroup(index, {
                                  ...group,
                                  source_dish_ids: selected
                                    ? (group.source_dish_ids || []).filter(id => id !== dish.id)
                                    : [...(group.source_dish_ids || []), dish.id],
                                })}
                                className="min-w-0 flex-1 text-left"
                              >
                                <span className="block font-medium text-[#2A2A28]">{dish.name}</span>
                                <span className="block text-xs text-[#7A7A75]">{getCatName(dish.category_id)}</span>
                              </button>
                              <div className="w-full lg:w-[170px] lg:flex-none">
                                <div className={`rounded-xl border px-3 py-2 ${selected ? 'border-[#5B6B3F]/30 bg-white/90' : 'border-[#EBE8E0] bg-[#FAF8F5]'}`}>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#7A5B12]">+</span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      className="h-9 rounded-lg border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
                                      value={configuredExtra}
                                      placeholder="0"
                                      onChange={e => updateFormulaDishExtraPrice(index, group, dish.id, e.target.value)}
                                      onClick={(event) => event.stopPropagation()}
                                    />
                                    <span className="text-sm text-[#7A5B12]">EUR</span>
                                  </div>
                                </div>
                                <p className="mt-1 text-[11px] text-[#9A968C]">
                                  Prix normal: {getDishReferencePrice(dish)} EUR
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-[#7A7A75]">
                      {(group.source_dish_ids || []).length} plat(s) autorise(s) dans cette sous-categorie
                    </p>
                    <p className="mt-1 text-xs text-[#7A7A75]">
                      Au-dela du nombre inclus, le client pourra reprendre un article et il sera facture au prix normal du plat.
                    </p>
                    <Button type="button" variant="outline" className="mt-3 rounded-lg border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => removeFormulaGroup(index)}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer ce choix
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={form.available} onCheckedChange={v => setForm({...form, available: v})} data-testid="dish-form-available" />
              <Label className="text-sm">Disponible</Label>
            </div>
            <Button onClick={save} className="w-full rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="dish-form-save">{editDish ? 'Modifier' : 'Creer'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editCategory ? 'Modifier la categorie' : 'Nouvelle categorie'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Nom</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={categoryForm.name} onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })} /></div>
            <div><Label className="text-sm">Description</Label><Textarea className="mt-1 rounded-xl border-[#EBE8E0]" value={categoryForm.description} onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })} /></div>
            <div><Label className="text-sm">Image</Label><Input className="mt-1 rounded-xl border-[#EBE8E0]" value={categoryForm.image} onChange={e => setCategoryForm({ ...categoryForm, image: e.target.value })} placeholder="Optionnel" /></div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="flex items-center justify-between rounded-xl border border-[#EBE8E0] p-3"><Label className="text-sm">Sur place</Label><Switch checked={categoryForm.visible_dine_in !== false} onCheckedChange={checked => setCategoryForm({ ...categoryForm, visible_dine_in: checked })} /></div>
              <div className="flex items-center justify-between rounded-xl border border-[#EBE8E0] p-3"><Label className="text-sm">Livraison</Label><Switch checked={categoryForm.visible_delivery !== false} onCheckedChange={checked => setCategoryForm({ ...categoryForm, visible_delivery: checked })} /></div>
              <div className="flex items-center justify-between rounded-xl border border-[#EBE8E0] p-3"><Label className="text-sm">Emporter</Label><Switch checked={categoryForm.visible_takeaway !== false} onCheckedChange={checked => setCategoryForm({ ...categoryForm, visible_takeaway: checked })} /></div>
            </div>
            <div>
              <Label className="text-sm">Ordre d'affichage</Label>
              <Input type="number" className="mt-1 rounded-xl border-[#EBE8E0]" value={categoryForm.order} onChange={e => setCategoryForm({ ...categoryForm, order: e.target.value })} />
              <p className="mt-1 text-xs text-[#7A7A75]">Astuce: pour reordonner plus facilement, ferme cette fenetre et glisse-depose les cartes de categories.</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={categoryForm.active} onCheckedChange={value => setCategoryForm({ ...categoryForm, active: value })} />
              <Label className="text-sm">Visible sur le site</Label>
            </div>
            <Button onClick={saveCategory} className="w-full rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white">{editCategory ? 'Modifier' : 'Creer'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
